import { useTranslation } from 'react-i18next';

/**
 * Custom hook that returns a sorted list of timezones with their offsets.
 *
 * @returns {Object} - An object containing the sorted timezone list.
 */
export const useTimezonesWithOffsets = () => {
  const { t } = useTranslation();

  /**
   * The list of supported timezones.
   *
   * @type {Array<Object>}
   */
  const timezoneList = Intl.supportedValuesOf('timeZone').map((timeZone) => {
    const timeZoneName = Intl.DateTimeFormat('en', {
      timeZone: timeZone,
      timeZoneName: 'longOffset',
    })
      .formatToParts()
      .find((item) => item.type === 'timeZoneName').value;

    const gmt =
      timeZoneName.length === 3
        ? `(${timeZoneName}+0:00)`
        : `(${timeZoneName})`;

    return {
      label: `${gmt} ${t(
        `timezone_list.${timeZone.replaceAll('/', '_').toLowerCase()}`,
      )}`,
      value: timeZone,
    };
  });

  /**
   * The sorted list of timezones by their labels.
   *
   * @type {Array<Object>}
   */
  const timezoneListSortByAlphabet = timezoneList.sort((a, b) => {
    if (a.label < b.label) {
      return -1;
    }
    if (a.label > b.label) {
      return 1;
    }
    return 0;
  });

  /**
   * The sorted list of timezones by their GMT offsets.
   *
   * @type {Array<Object>}
   */
  const timezoneListSortByGMT = timezoneListSortByAlphabet.sort((a, b) => {
    const firstItemGmt = Number(
      a.label.replaceAll(':', '.').slice('4', a.label.indexOf(')')),
    );
    const secondItemGmt = Number(
      b.label.replaceAll(':', '.').slice('4', b.label.indexOf(')')),
    );

    if (firstItemGmt < secondItemGmt) {
      return -1;
    }
    if (firstItemGmt > secondItemGmt) {
      return 1;
    }
    return 0;
  });

  return { timezoneListSortByGMT };
};
